import { useIsFocused } from '@react-navigation/native';
import { FlashList } from '@shopify/flash-list';
import { Formik } from 'formik';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, RefreshControl } from 'react-native';
import { InferType } from 'yup';

import ChatCard from '@components/Chat/ChatCard.web';
import { Scrollbar } from '@components/Hoverable/Scrollbar';
import { ChatModal } from '@components/Modals/ChatModal.web';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import { Box } from '@components/Restyle';
import {
  Chat,
  ListChatsPaginationDocument,
  TutoralizationSetting,
  useArchiveChatMutation,
  useDeleteGroupChatMutation,
  useLeaveChatMutation,
  useUnarchiveChatMutation,
  useUpdateChatMutation,
} from '@graphql/generated';
import useChatInput from '@hooks/useChatInput';
import useEmoji from '@hooks/useEmoji';
import useFileProcessor from '@hooks/useFileProcessor';
import useMe from '@hooks/useMe';
import { useCloseOnOutsideClick } from '@hooks/useOutsideClick.web';
import { groupChatSettingsSchema } from '@schemas/groupChatSettingsSchema';
import { ChatRelatedTags } from '@screens/Chats/ChatRelatedTags.web';
import { GroupChatMembers } from '@screens/Chats/GroupChatMembers.web';
import { GroupChatSettingsProfile } from '@screens/Chats/GroupChatSettingsProfile.web';
import { TutorialItem } from '@screens/Projects/AllProjects.web';
import {
  archiveChatCacheUpdate,
  unarchiveChatCacheUpdate,
} from '@utils/chat/chatArchiveCache';
import { AsyncStorage } from '@utils/storage';

export type GroupChatSettingsValues = InferType<typeof groupChatSettingsSchema>;

type ChatListProps = {
  chats: Chat[];
  onPress?: (chat: Chat) => void;
  onPinPress?: (chat: Chat) => void;
  onMutePress?: (chat: Chat) => void;
  isEditing: boolean;
  selected?: Chat[];
  /** TA-10892 #33: chats of members the Owner invited during onboarding. */
  invitedChatIds?: Set<string>;
  loading: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  hideSeparatorItem?: boolean;
  filterVal?: string;
  open: boolean;
  hasNextPage: boolean;
  loadMore: () => void;
  fetchChats: () => void;
  isArchiveList?: boolean;
};

// FlashList's `estimatedItemSize` is the estimated height of a SINGLE row, not
// the whole list. It must stay constant: scaling it by the chat count inflates
// it to 200 * N, which breaks FlashList's virtualization math on large chat
// lists — the window collapses to zero visible rows and the renderer can hang
// while laying out a multi-million-pixel virtual canvas.
export const CHAT_LIST_ESTIMATED_ITEM_SIZE = 200;

const Tutorial: TutorialItem[] = [
  {
    title: 'Chat that works like your project hub',

    description:
      'Conversations shouldn’t get lost. Unlike other chats, messages here are directly tied to your projects and tasks—so every discussion leads to action.',

    currentStep: 1,
    img: 'content1.png',
  },

  {
    title: 'Collaboration starts with your team',

    description:
      'Keep everything and everyone in one track. Powerful collaboration, powerful results.',

    currentStep: 2,
    img: 'content2.png',
  },
  {
    title: 'Turn conversations into action',

    description:
      'Tag a message to a project or task, and it will automatically save  to the project’s file drawer and activity log—so nothing gets lost.',

    currentStep: 3,
    img: 'content3.png',
  },
];
export const ChatList: React.FC<ChatListProps> = ({
  chats,
  onPress,
  isEditing,
  selected,
  invitedChatIds,
  loading,
  refreshing = false,
  onRefresh,
  hideSeparatorItem,
  filterVal,
  open,
  hasNextPage,
  loadMore,
  fetchChats,
  isArchiveList,
}) => {
  const { me } = useMe();
  const ref_flashlist = useRef<FlashList<Chat>>(null);
  const { t } = useTranslation();
  const [xPos, setXPos] = useState(0);
  const [yPos, setYPos] = useState(0);
  const [showMenu, setShowMenu] = useState(false);
  const { showMessageEmojiModal, setShowMessageEmojiModal } = useEmoji();
  const fadeAnim = useState(new Animated.Value(0))[0];
  const mainRef = useRef<HTMLElement>(null);
  const [bodyWidth, setBodyWidth] = useState(0);
  const [bodyHeight, setBodyHeight] = useState(0);
  const { selectedChat: chat, setSelectedChat } = useChatInput();
  const { name, description, avatar, settingsPin, settingsMute } = chat || {};
  const initialValues: GroupChatSettingsValues = {
    name: name || '',
    description: description || '',
    avatar: avatar || null,
  };
  const isFocused = useIsFocused();

  const [mute, setMute] = useState(!!settingsMute);
  const [pin, setPin] = useState(!!settingsPin);
  useEffect(() => {
    setPin(!!settingsPin);
    setMute(!!settingsMute);
  }, [settingsPin, settingsMute]);
  const [updateChat, { loading: loadingUpdateChat }] = useUpdateChatMutation();
  const [bannerDismissed, setBannerDismissed] = useState(true);

  const { processFiles } = useFileProcessor();

  // Track which chat the edit modal was opened for, so clearing/switching the
  // selected chat closes it instead of re-binding the form to another chat.
  const [editingChatId, setEditingChatId] = useState<string>();
  // `selectedChat` is local state, never a transient query miss, so a mismatch
  // means the user left the chat being edited: drop the edit intent instead of
  // keeping it primed to re-open when that chat is selected again.
  if (editingChatId && chat?.id !== editingChatId) {
    setEditingChatId(undefined);
  }
  const [submitting, setSubmitting] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showUnarchiveModal, setShowUnarchiveModal] = useState(false);
  const [showLeaveChatModal, setShowLeaveChatModal] = useState(false);
  const [isShowChatRelatedTags, setIsShowChatRelatedTags] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);

  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const [leaveChat, { data: leaveChatData }] = useLeaveChatMutation({
    refetchQueries: [
      { query: ListChatsPaginationDocument, variables: { first: 20 } },
    ],
  });

  useEffect(() => {
    if (!leaveChatData) return;
    setShowLeaveChatModal(false);
  }, [leaveChatData]);

  const [archiveChat, { data: archiveChatData }] = useArchiveChatMutation({
    refetchQueries: [
      { query: ListChatsPaginationDocument, variables: { first: 20 } },
    ],
    // TA-10878: handleArchiveChat passes its own refetchQueries, which
    // REPLACES the list above — only the archived list is refetched, so
    // without this the row stays in the active list. Declared on the hook
    // (not at the call site) so it cannot be overridden the same way.
    update: archiveChatCacheUpdate,
  });

  useEffect(() => {
    if (!archiveChatData) return;
    setShowArchiveModal(false);
    onRefreshRef.current();
  }, [archiveChatData]);

  const [unarchiveChat, { data: unarchiveChatData }] = useUnarchiveChatMutation(
    {
      refetchQueries: [
        { query: ListChatsPaginationDocument, variables: { first: 20 } },
      ],
      update: unarchiveChatCacheUpdate,
    }
  );

  useEffect(() => {
    if (!unarchiveChatData) return;
    setShowUnarchiveModal(false);
    onRefreshRef.current();
  }, [unarchiveChatData]);

  const [deleteGroup, { data: deleteGroupData }] = useDeleteGroupChatMutation({
    refetchQueries: [
      { query: ListChatsPaginationDocument, variables: { first: 20 } },
    ],
  });

  useEffect(() => {
    if (!deleteGroupData) return;
    setShowDeleteGroupModal(false);
  }, [deleteGroupData]);

  const handleLeaveChat = () => {
    chat?.id &&
      leaveChat({
        variables: {
          id: chat?.id,
        },
      });
  };

  const handleArchiveChat = () => {
    chat?.id &&
      archiveChat({
        variables: {
          id: chat?.id,
        },
        refetchQueries: [
          {
            query: ListChatsPaginationDocument,
            variables: {
              archivesOnly: true,
              first: 20,
            },
          },
        ],
      });
  };

  const handleUnarchiveChat = () => {
    chat?.id &&
      unarchiveChat({
        variables: {
          id: chat?.id,
        },
        refetchQueries: [
          {
            query: ListChatsPaginationDocument,
            variables: {
              archivesOnly: true,
              first: 20,
            },
          },
          {
            query: ListChatsPaginationDocument,
            variables: {
              archivesOnly: false,
              first: 20,
            },
          },
        ],
      });
  };

  const handleDeleteGroup = () => {
    deleteGroup({
      variables: {
        id: chat?.id,
      },
    });
  };

  const handlePinToggle = (newPin: boolean | undefined) => {
    if (newPin === undefined) {
      return;
    }

    if (!loadingUpdateChat && chat?.id) {
      setPin(newPin);
      setSelectedChat({ ...chat, settingsPin: newPin });
      updateChat({
        variables: {
          id: chat.id,
          attributes: {
            pin: newPin,
          },
        },
        optimisticResponse: {
          updateChat: {
            ...chat,
            settingsPin: newPin,
            settingsMute: !!chat.settingsMute,
          },
        },
      }).then(() => {
        onRefresh();
      });
    }
  };

  const handleMuteToggle = (newMute: boolean | undefined) => {
    if (newMute === undefined) {
      return;
    }
    if (!loadingUpdateChat && chat?.id) {
      setMute(newMute);
      updateChat({
        variables: {
          id: chat.id,
          attributes: {
            mute: newMute,
          },
        },
      }).then(() => {
        onRefresh();
      });
    }
  };

  const saveChanges = (values: GroupChatSettingsValues) => {
    const newChatName = values.name ? values.name : name;
    const newAvatar = values.avatar !== avatar ? values.avatar : undefined;
    const newChatDescription = values.description
      ? values.description
      : description;

    if (values.avatar && me && newAvatar) {
      const {
        name: avatarName,
        clientId,
        contentType,
        isImage,
        size,
        isAudio,
        duration,
        isPreviewable,
        thumbnail,
      } = values.avatar;

      const doc: Document = {
        __typename: 'Document',
        id: 'chat_avatar',
        owner: me,
        name: avatarName,
        clientId,
        contentType: contentType,
        isImage,
        createdAt: new Date().toISOString(),
        size,
        file: {
          __typename: 'File',
          cdnBaseUrl: '',
          path: '',
          id: clientId,
          url: '',
          ...values.avatar,
          isPreviewable: isPreviewable,
          thumbnail: thumbnail,
        },
        isAudio,
        isPreviewable,
        thumbnail,
        duration,
      };
      const documents = [doc];
      processFiles(documents)
        .then((response) => {
          const { blobId } = response[0];
          if (!loadingUpdateChat)
            updateChat({
              variables: {
                id: chat?.id || '',
                attributes: {
                  name: newChatName,
                  description: newChatDescription,
                  avatarBlobId: blobId,
                },
              },
            }).then(() => {
              onRefresh();
            });
        })
        .catch((_err) => {
          setSubmitting(false);
        });
    } else {
      updateChat({
        variables: {
          id: chat?.id || '',
          attributes: {
            name: newChatName || '',
            description: newChatDescription,
          },
        },
      }).then(() => {
        onRefresh();
      });
    }
    setEditingChatId(undefined);
    setSubmitting(true);
  };

  const handleClick = () => {
    setTimeout(() => {
      showMessageEmojiModal && setShowMessageEmojiModal(false);
      setShowMenu(false);
    }, 100);
  };

  const handleContextMenu = (e: any) => {
    e.preventDefault();
  };

  useEffect(() => {
    const element = mainRef.current;
    if (element) {
      element.addEventListener('click', handleClick);
      element.addEventListener('contextmenu', handleContextMenu);
      return () => {
        element.removeEventListener('click', handleClick);
        element.removeEventListener('contextmenu', handleContextMenu);
      };
    }
  }, []);

  const menuRef = useCloseOnOutsideClick<HTMLDivElement>(
    showMenu,
    () => {
      setTimeout(() => {
        setShowMenu(false);
      }, 100);
    },
    { eventType: 'mousedown' }
  );

  useEffect(() => {
    if (isFocused) {
      fetchChats();
    }
  }, [isFocused]);

  useEffect(() => {
    const getBannerStatusFromStorage = async () => {
      const chatBannerInStorage = await AsyncStorage.getItem(
        'chatBannerDismissed'
      );
      const bannerDismissedFromTutorial = me?.tutoralizationSetting?.find(
        (item) => item.settingType === TutoralizationSetting.Chat
      )?.close;

      if (!bannerDismissedFromTutorial && !chatBannerInStorage) {
        setBannerDismissed(false);
      }
    };
    getBannerStatusFromStorage();
  }, [me?.tutoralizationSetting?.length]);

  useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: showMenu ? 1 : 0,
      duration: 100,
      useNativeDriver: false,
    }).start();
  }, [showMenu, fadeAnim]);

  const getMenuListLength = (chat: Chat) => {
    const { owner, settings, leaveOn } = chat || {};

    const ownerId = owner?.id;
    const isOwner = me?.id === ownerId;
    const isArchived = settings?.archivedAt;
    const isGroupChat = chat?.isGroupChat;

    let i = 0;
    if (isGroupChat && !leaveOn) i = i + 1; // Edit

    if (isGroupChat) i = i + 1; //Chat Members

    i = i + 1; //Chat Related Tags

    if (!leaveOn) i = i + 2; //Mute and Pin Chat

    if (isGroupChat && !isArchived && isOwner) i = i + 1; //Archive For Everyone

    if (!isGroupChat && !isArchived) i = i + 1; //Archive Chat

    i = i + 1; //Clear Chat History

    if (isGroupChat && !isOwner && !isArchived && !leaveOn) i = i + 1; //Leave Chat

    if (isGroupChat && leaveOn) i = i + 1; //Delete Group Chat

    return i;
  };

  const onRightClick = useCallback(
    (chat: Chat, mouseX: number, mouseY: number) => {
      setShowMenu(false);
      setSelectedChat(chat);
      const menuListLength = getMenuListLength(chat);
      if (bodyHeight - mouseY < 240) {
        mouseY = mouseY - (menuListLength + 1) * 40 - 12;
      }
      const element = mainRef.current;
      if (element) {
        const { left, top } = element.getBoundingClientRect();
        mouseX = mouseX - left;
        mouseY = mouseY - top;
      }
      if (bodyWidth - mouseX < 270) {
        mouseX = mouseX - 281;
      }
      setXPos(mouseX);
      setYPos(mouseY);
      setTimeout(() => {
        setShowMenu(true);
      }, 100);
    },
    [bodyWidth, bodyHeight]
  );

  const onRightPinPress = (chat: Chat) => {
    const { id, settingsPin, settingsMute } = chat;

    updateChat({
      variables: {
        id,
        attributes: {
          pin: !settingsPin,
        },
      },
      optimisticResponse: {
        updateChat: {
          ...chat,
          settingsPin: !settingsPin,
          settingsMute: !!settingsMute,
        },
      },
    }).then(() => {
      onRefresh();
    });
  };

  const onRightMutePress = (chat: Chat) => {
    const { id, settingsPin, settingsMute } = chat;

    updateChat({
      variables: {
        id,
        attributes: {
          mute: !settingsMute,
        },
      },
      optimisticResponse: {
        updateChat: {
          ...chat,
          settingsPin: !!settingsPin,
          settingsMute: !settingsMute,
        },
      },
    }).then(() => {
      onRefresh();
    });
  };

  const renderListFooter = useCallback(
    () => <Box paddingBottom='listFooter' />,
    [open, isArchiveList, chats?.length, bannerDismissed, Tutorial?.length]
  );

  const renderFlashList = (
    showsVerticalScrollIndicator: boolean,
    setContentOffset?: (offset: { x: number; y: number }) => void,
    setContentSize?: (size: number) => void
  ) => {
    return (
      <>
        {/* Test-only marker exposing the real chats.length driving
            estimatedItemSize below — see CHAT_LIST_ESTIMATED_ITEM_SIZE's own
            comment for why this must stay a constant regardless of count
            (TA-10382). DOM-rendered row count is capped by virtualization and
            cannot stand in for this value from a test. */}
        <Box
          testID={`chat-list-total-${chats?.length ?? 0}`}
          style={{ width: 0, height: 0, overflow: 'hidden' }}
        />
        <FlashList
          extraData={[selected, isEditing, invitedChatIds]}
          ref={ref_flashlist}
          showsVerticalScrollIndicator={showsVerticalScrollIndicator}
          data={chats}
          renderItem={({ item, index }) => (
            <Box
              accessibilityLabel={isEditing ? 'Edit chat item' : 'Chat item'}>
              <ChatCard
                filterVal={filterVal}
                chatCard={item}
                onMutePress={onRightMutePress}
                onPinPress={onRightPinPress}
                onPress={onPress}
                isEditing={isEditing}
                isSelected={selected?.some((i) => i.id === item.id)}
                isInvited={invitedChatIds?.has(item.id)}
                collapsed={!open}
                onRightClick={onRightClick}
                contentHeightLimit={true}
              />
              {!open && item?.settingsPin && !chats[index + 1]?.settingsPin && (
                <Box
                  borderBottomWidth={1}
                  borderBottomColor='grey02'
                  marginHorizontal='s'
                  style={{ marginTop: -1 }}
                />
              )}
            </Box>
          )}
          estimatedItemSize={CHAT_LIST_ESTIMATED_ITEM_SIZE}
          keyExtractor={(item: Chat) => item.id}
          ItemSeparatorComponent={
            hideSeparatorItem
              ? undefined
              : () => (
                  <Box
                    borderBottomWidth={1}
                    borderBottomColor='grey01'
                    marginHorizontal='m'
                  />
                )
          }
          refreshing={loading}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          ListFooterComponent={renderListFooter}
          onContentSizeChange={(_, height) =>
            setContentSize && setContentSize(height)
          }
          onScroll={({ nativeEvent: { contentOffset } }) =>
            chats?.length > 1 && // added to avoid scroll offset change if chat has more one msg
            setContentOffset &&
            setContentOffset(contentOffset)
          }
          onEndReached={hasNextPage ? loadMore : undefined}
        />
      </>
    );
  };

  return (
    <Box
      flex={1}
      ref={mainRef}
      onLayout={(e: any) => {
        if (e.nativeEvent.layout.width > 0) {
          setBodyWidth(e.nativeEvent.layout.width);
          setBodyHeight(e.nativeEvent.layout.height);
        }
      }}>
      <Scrollbar
        HoverableProps={{
          style: {
            flex: 1,
            alignContent: 'center',
            justifyContent: 'center',
            display: 'flex',
          },
        }}>
        {({ setContentOffset, setContentSize, scrollTo }) => {
          scrollTo !== undefined &&
            ref_flashlist.current?.scrollToOffset({ offset: scrollTo });
          return renderFlashList(false, setContentOffset, setContentSize);
        }}
      </Scrollbar>

      {showMenu && (
        <Animated.View
          ref={menuRef}
          style={{
            width: 281,
            position: 'absolute',
            opacity: fadeAnim,
            top: yPos,
            left: xPos,
          }}>
          <ChatModal
            onDismiss={() => {
              setShowMenu(false);
            }}
            onEditPress={() => {
              setEditingChatId(chat?.id);
            }}
            onChatMembersPress={() => {
              setShowAddMembersModal(true);
            }}
            onRelatedTagsPress={() => {
              setIsShowChatRelatedTags(true);
            }}
            onMuteChatPress={() => {
              handleMuteToggle(!mute);
            }}
            onPinChatPress={() => {
              handlePinToggle(!pin);
            }}
            onArchiveChatPress={() => {
              setShowArchiveModal(true);
            }}
            onUnarchiveChatPress={() => {
              setShowUnarchiveModal(true);
            }}
            onLeaveChatPress={() => {
              setShowLeaveChatModal(true);
            }}
            onDeleteChatPress={() => {
              setShowDeleteGroupModal(true);
            }}
          />
        </Animated.View>
      )}
      {editingChatId && chat?.id === editingChatId && (
        <Formik<GroupChatSettingsValues>
          validateOnMount={true}
          initialValues={initialValues}
          onSubmit={saveChanges}
          validationSchema={groupChatSettingsSchema}>
          <GroupChatSettingsProfile
            chat={chat}
            loading={submitting}
            onClose={() => setEditingChatId(undefined)}
          />
        </Formik>
      )}
      {isShowChatRelatedTags && (
        <ChatRelatedTags
          chatId={chat?.id}
          onClose={() => setIsShowChatRelatedTags(false)}
        />
      )}

      {showAddMembersModal && chat?.id && (
        <GroupChatMembers
          chatId={chat.id}
          showAddMembersModal={showAddMembersModal}
          onClose={() => setShowAddMembersModal(false)}
        />
      )}
      {showArchiveModal && (
        <ConfirmModal
          showModal={showArchiveModal}
          onClose={() => setShowArchiveModal(false)}
          onPress={handleArchiveChat}
          buttonText={t('shared:archiveChat')}
          title={t('shared:archive')}
          message={t('models:chat.archive.message')}
        />
      )}
      {showUnarchiveModal && (
        <ConfirmModal
          showModal={showUnarchiveModal}
          onClose={() => setShowUnarchiveModal(false)}
          onPress={handleUnarchiveChat}
          buttonText={t('models:chat.restore.cta')}
          title={t('models:chat.restore.cta')}
          message={t('models:chat.restore.message')}
        />
      )}
      {showLeaveChatModal && (
        <ConfirmModal
          showModal={showLeaveChatModal}
          onClose={() => setShowLeaveChatModal(false)}
          onPress={handleLeaveChat}
          buttonText={t('models:chat.leaveGroup.cta')}
          title={t('models:chat.leaveGroup.cta')}
          message={t('models:chat.leaveGroup.message')}
        />
      )}
      {showDeleteGroupModal && (
        <ConfirmModal
          showModal={showDeleteGroupModal}
          onClose={() => setShowDeleteGroupModal(false)}
          onPress={handleDeleteGroup}
          buttonText={t('models:chat.deleteGroup.cta')}
          title={t('models:chat.deleteGroup.cta')}
          message={t('models:chat.deleteGroup.message')}
        />
      )}
    </Box>
  );
};
